/*
 * LAYERING
 *
 * Dialogs sit at z-index 1100, above the app's chrome. The fixed sidebar is z-index 199
 * (styles/scss/layout/_sidebar.scss), so the previous default of 50 put every dialog UNDERNEATH it: a
 * wide dialog had its left edge covered by the nav, and the overlay did not dim the nav either.
 *
 * Call sites kept discovering this one at a time and patching it with their own number - 200, 201, 300,
 * 400, 1060 and 9999 all appeared in the codebase. Do not add another. If a dialog is being covered, the
 * fix belongs here, not in a className.
 *
 * The scale, so the next person does not have to reverse engineer it:
 *
 *     199    app chrome (the fixed sidebar)
 *     1030   legacy page chrome in the SCSS: sticky .site-footer and design-library pagination
 *     1060   .bee-check-panel, fixed, in the email designer
 *     1100   dialogs: DialogContent, DraggableDialogContent, and their overlay. Above the two tiers
 *            above, which a dialog at 1000 rendered underneath - on a narrow viewport the sticky footer
 *            painted over the backdrop and clipped the dialog's own buttons.
 *     1200   portaled overlays that can open from INSIDE a dialog - tooltip, popover, dropdown-menu.
 *            They have to clear the dialog or they render behind the thing that opened them.
 *     1300   the session-timeout warning (components/Shared/WarningModal). Mounted globally, not a Radix
 *            dialog, so it does not stack with them and has to outrank the whole scale: being logged out
 *            without seeing the countdown is worse than any dialog being covered. It also sets
 *            pointer-events-auto, because Radix disables pointer events on body while a dialog is open.
 *     10000  react-select menus (portaled to body, already set in select.jsx)
 *     99999  iziToast, which is what utils/toast actually renders
 *
 * A nested dialog that must sit above its parent is the one legitimate override, and it has to set BOTH
 * halves: `zIndex` for the content and `overlayClassName` for the overlay. Setting only the className is
 * the trap - twMerge resolves it over the base, the overlay keeps 1100, and the dialog renders
 * underneath its own backdrop, dimmed and click-blocked.
 */
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, style, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn("tw:fixed tw:inset-0 tw:z-[1100] tw:bg-black/50 tw:backdrop-blur-sm", className)} style={{ animation: 'none', ...style }} {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn("tw:fixed tw:left-[50%] tw:top-[50%] tw:z-[1100] tw:grid tw:w-full tw:max-w-lg tw:translate-x-[-50%] tw:translate-y-[-50%] tw:gap-4 tw:border tw:border-gray-200 tw:bg-white tw:p-6 tw:shadow-modal tw:sm:rounded-card", className)}
      style={{ animation: 'none' }}
      {...props}
    >
      {children}
      {/* Radix renders a plain <button>, which the unlayered Bootstrap reboot then gives its own grey
          fill and border. Background, border and shadow are forced off rather than set, because a
          layered Tailwind utility loses that argument. */}
      <DialogPrimitive.Close className="tw:absolute tw:right-4 tw:top-4 tw:inline-flex tw:items-center tw:justify-center tw:h-7 tw:w-7 tw:p-0 tw:rounded-md tw:cursor-pointer tw:text-gray-500 tw:!bg-transparent tw:!border-0 tw:!shadow-none tw:opacity-70 tw:transition-opacity tw:hover:opacity-100 tw:hover:!bg-gray-100 tw:hover:text-gray-900 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-secondary tw:focus:ring-offset-2 tw:disabled:pointer-events-none">
        <X className="tw:h-4 tw:w-4" />
        <span className="tw:sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }) => (<div className={cn("tw:flex tw:flex-col tw:space-y-1.5 tw:text-center tw:sm:text-left", className)} {...props} />)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }) => (<div className={cn("tw:flex tw:flex-col-reverse tw:gap-3 tw:sm:flex-row tw:sm:justify-end", className)} {...props} />)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn("tw:!text-lg tw:!font-semibold tw:!normal-case tw:leading-none tw:tracking-tight tw:text-gray-900", className)} {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("tw:text-sm tw:text-gray-500", className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export { Dialog, DialogPortal, DialogOverlay, DialogTrigger, DialogClose, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription }
